import { ShieldAlert, Check } from "lucide-react";
import { t } from "@/lib/i18n";

/**
 * First-run disclaimer (spec 06 §2 screen 5).
 *
 * The legality overlay is OSM-derived, possibly incomplete, and **not legally
 * binding**. The user must acknowledge once; it's reachable later from the map
 * menu. The `required` variant is non-dismissable until acknowledged.
 *
 * Acknowledgement is persisted in localStorage (spec 06 §1: localStorage only
 * for UI prefs).
 */
export const DISCLAIMER_ACK_KEY = "bubatzspot.disclaimer.acked.v1";

export function isDisclaimerAcknowledged(): boolean {
  try {
    return localStorage.getItem(DISCLAIMER_ACK_KEY) === "1";
  } catch {
    return false;
  }
}

export function acknowledgeDisclaimer(): void {
  try {
    localStorage.setItem(DISCLAIMER_ACK_KEY, "1");
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export interface DisclaimerProps {
  /** true → first-run gate (no backdrop dismiss); false → menu-reopened view. */
  required: boolean;
  onAccept: () => void;
}

export function Disclaimer({ required, onAccept }: DisclaimerProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center fade-in">
      {/* Non-dismissable backdrop when required. */}
      {required ? (
        <div className="absolute inset-0 bg-black/80" />
      ) : (
        <button
          type="button"
          aria-label={t("create.cancel")}
          onClick={onAccept}
          className="absolute inset-0 bg-black/60"
        />
      )}
      <div
        className="relative mx-4 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-bg-elevated p-6 shadow-2xl"
        role="dialog"
        aria-modal={required}
      >
        <div className="mb-3 flex items-center gap-2">
          <ShieldAlert size={24} className="text-legal-amber" />
          <h2 className="text-lg font-bold text-fg">{t("disclaimer.title")}</h2>
        </div>

        <div className="space-y-2 text-sm text-fg-muted">
          <p>{t("disclaimer.p1")}</p>
          <p>{t("disclaimer.p2")}</p>
          <p>{t("disclaimer.p3")}</p>
        </div>

        <button
          type="button"
          onClick={() => {
            acknowledgeDisclaimer();
            onAccept();
          }}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-sm font-semibold text-accent-contrast"
        >
          <Check size={16} />
          {t("disclaimer.accept")}
        </button>
      </div>
    </div>
  );
}
